import React, { useCallback, useEffect, useState } from 'react';
import { societyService } from '../../../services/societyService';

/**
 * Signup society picker: debounced server search, full city list from DB (incl. non-public for registration),
 * and flow to suggest a missing society/area.
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - city: string (required when open)
 * - requesterEmail: string (from step 1, optional)
 * - onSelect: (societyId: string, options?: { name?: string }) => void — optional name when server created/matched
 * - stackedAboveParentModal: when true, raise z-index so this dialog appears above another modal (e.g. city picker).
 */
const SocietyPickerModal = ({
  open,
  onClose,
  city,
  requesterEmail = '',
  onSelect,
  maxPanelHeightPx = null,
  initialMode = 'pick',
  initialName = '',
  stackedAboveParentModal = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [mode, setMode] = useState('pick');
  const [addName, setAddName] = useState('');
  const [addDetails, setAddDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const resetTransient = useCallback(() => {
    setSearchQuery('');
    setList([]);
    setFetchError('');
    setMode('pick');
    setAddName('');
    setAddDetails('');
    setSubmitError('');
  }, []);

  useEffect(() => {
    if (!open) {
      resetTransient();
    }
  }, [open, resetTransient]);

  useEffect(() => {
    if (!open) return;
    if (initialMode === 'add') {
      setMode('add');
      setAddName((initialName || '').trim());
      setSubmitError('');
      return;
    }
    setMode('pick');
  }, [open, initialMode, initialName]);

  useEffect(() => {
    if (!open) return undefined;
    const onEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !city) {
      return undefined;
    }
    setLoading(true);
    setFetchError('');
    const timer = setTimeout(() => {
      (async () => {
        try {
          const rows = await societyService.getSocietiesForCityAll(city, searchQuery, {
            forRegistration: true,
          });
          rows.sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
          );
          setList(rows);
        } catch (e) {
          setFetchError(e?.message || 'Could not load societies. Try again.');
          setList([]);
        } finally {
          setLoading(false);
        }
      })();
    }, 300);
    return () => clearTimeout(timer);
  }, [open, city, searchQuery]);

  const handleSubmitSuggestion = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!(addName || '').trim()) {
      setSubmitError('Enter the society or area name.');
      return;
    }
    setSubmitting(true);
    try {
      const body = await societyService.submitSocietyAreaSuggestion({
        requested_name: addName,
        city,
        requester_email: requesterEmail,
        details: addDetails,
      });
      // axios interceptor unwraps ApiResponse to payload { society, matched_existing, ... }
      const sid = body?.society?.id;
      if (sid != null) {
        onSelect(String(sid), { name: body?.society?.name });
        onClose();
        return;
      }
      setSubmitError('Unexpected response. Please try again.');
    } catch (err) {
      setSubmitError(err?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return null;
  }

  const overlayClassName = stackedAboveParentModal
    ? 'fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50'
    : 'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50';

  return (
    <div
      className={overlayClassName}
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[85vh] flex flex-col"
        style={maxPanelHeightPx ? { maxHeight: `${maxPanelHeightPx}px` } : undefined}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="society-picker-title"
      >
        {mode === 'pick' ? (
          <>
            <div className="p-4 border-b border-gray-200">
              <h3 id="society-picker-title" className="text-lg font-semibold text-gray-900">
                Select your society
              </h3>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search societies..."
                className="mt-3 w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
                aria-label="Search societies"
              />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-2">
              {fetchError && (
                <p className="py-3 px-2 text-sm text-red-600" role="alert">
                  {fetchError}
                </p>
              )}
              {loading && !fetchError && (
                <p className="py-4 text-center text-gray-500 text-sm">Loading…</p>
              )}
              {!loading && !fetchError && list.length === 0 && (
                <p className="py-4 text-center text-gray-500 text-sm">
                  {searchQuery.trim()
                    ? 'No societies match your search in this city.'
                    : 'No societies found for this city yet.'}
                </p>
              )}
              {!loading && !fetchError && list.length > 0 && (
                <ul className="space-y-0.5">
                  {list.map((society) => (
                    <li key={society.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onSelect(String(society.id), { name: society.name });
                          onClose();
                        }}
                        className="w-full px-3 py-2.5 text-left rounded-md hover:bg-gray-100 focus:bg-gray-100 focus:outline-none text-gray-900"
                      >
                        {society.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {!loading && !fetchError && list.length === 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setAddName((searchQuery || '').trim());
                    setMode('add');
                    setSubmitError('');
                  }}
                  className="mt-2 w-full px-3 py-2.5 text-left rounded-md border border-dashed border-blue-400 text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
                >
                  Society Not Listed, Add here
                </button>
              )}
            </div>
            <div className="p-3 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-2">
              <h3 id="society-picker-title" className="text-lg font-semibold text-gray-900">
                Suggest a society or area
              </h3>
              <button
                type="button"
                onClick={() => {
                  setMode('pick');
                  setSubmitError('');
                }}
                className="text-sm text-blue-600 hover:text-blue-800 shrink-0"
              >
                Back
              </button>
            </div>
            <form onSubmit={handleSubmitSuggestion} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
              <p className="text-sm text-gray-600">
                Enter the name as you know it. We will match an existing society if possible, or add it so you can
                continue signing up.
              </p>
              <div>
                <label htmlFor="suggest-society-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Society or area name <span className="text-red-500">*</span>
                </label>
                <input
                  id="suggest-society-name"
                  type="text"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Bahria Town Phase 4"
                  maxLength={200}
                />
              </div>
              <div>
                <label htmlFor="suggest-society-details" className="block text-sm font-medium text-gray-700 mb-1">
                  Extra details (optional)
                </label>
                <textarea
                  id="suggest-society-details"
                  value={addDetails}
                  onChange={(e) => setAddDetails(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500"
                  placeholder="Nearby landmark, sector, etc."
                />
              </div>
              {submitError && (
                <p className="text-sm text-red-600" role="alert">
                  {submitError}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full px-4 py-2 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-60"
              >
                {submitting ? 'Checking…' : 'Continue with this society'}
              </button>
            </form>
            <div className="p-3 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SocietyPickerModal;
